import React, { useEffect, useState } from 'react';
import RedemptionPill from './RedemptionPill';

const menuItem = {
  display: 'block',
  width: '100%',
  padding: '8px 12px',
  backgroundColor: 'transparent',
  color: '#374151',
  border: 'none',
  fontFamily: '"Baikal Book"',
  fontSize: '14px',
  cursor: 'pointer',
  textAlign: 'left',
};

const badge = {
  padding: '1px 7px',
  borderRadius: '100px',
  backgroundColor: '#f3f4f6',
  color: '#6b7280',
  fontFamily: '"Baikal Book"',
  fontSize: '11px',
  fontWeight: 600,
};

const REDEMPTION_LABELS = {
  raffle: 'Raffle',
  auction: 'Auction',
  card: 'Card purchase',
  checkout: 'Points checkout',
  general: 'Points checkout',
};

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * One reward as a full-width preview card: everything the reward holds is on the
 * face of it, with a ⋮ menu for edit / activate / delete. Matches the first-reward
 * cards in FirstRewardsSection so both reward admin surfaces read the same.
 */
const StaffRewardTile = ({
  reward,
  getImageUrl,
  isActive = true,
  onToggleStatus,
  onEdit,
  onDelete,
  isCompleted = false,
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (event) => {
      if (!event.target.closest(`[data-reward-menu="${reward.id}"]`)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen, reward.id]);

  const imageUrls = (reward.images || [])
    .map((img) => getImageUrl(img.image_path))
    .filter(Boolean);

  const isAuction = reward.redemption_type === 'auction';
  const isRaffle = reward.redemption_type === 'raffle';

  const cashPrice =
    reward.redemption_type === 'card' &&
    reward.cash_price_cents != null &&
    Number(reward.cash_price_cents) >= 50
      ? `$${(Number(reward.cash_price_cents) / 100).toFixed(2)}`
      : null;

  // Only the rows this reward actually has — an auction has no drawing date, and
  // a plain checkout reward has neither.
  const detailRows = [];

  // Auctions price by bid, not by a fixed cost, so a "Points" row would read 0 and
  // mislead; their numbers live in the auction rows below instead.
  if (!isAuction) {
    detailRows.push(['Points', `${Number(reward.points_cost || 0).toLocaleString()} pts`]);
  }
  if (cashPrice) {
    detailRows.push(['Price', cashPrice]);
  }
  detailRows.push([
    'Redemption',
    REDEMPTION_LABELS[reward.redemption_type] || reward.redemption_type,
  ]);

  if (isRaffle) {
    // No drawing-date row: the RedemptionPill above the rows already carries it.
    detailRows.push(['Opens', formatDateTime(reward.raffle_info?.start_date)]);
  }

  if (isAuction) {
    detailRows.push(['Auction starts', formatDateTime(reward.auction_info?.start_date)]);
    detailRows.push(['Auction ends', formatDateTime(reward.auction_info?.end_date)]);
    if (reward.auction_info?.current_highest_bid != null) {
      detailRows.push(['Current bid', `${reward.auction_info.current_highest_bid} pts`]);
    }
    if (reward.auction_info?.starting_bid != null) {
      detailRows.push(['Starting bid', `${reward.auction_info.starting_bid} pts`]);
    }
    if (reward.auction_info?.minimum_increment != null) {
      detailRows.push(['Bid increment', `${reward.auction_info.minimum_increment} pts`]);
    }
  }

  if (Array.isArray(reward.gift_card_codes) && reward.gift_card_codes.length > 0) {
    detailRows.push([
      'Gift card codes',
      `${reward.gift_card_codes.length} code${reward.gift_card_codes.length === 1 ? '' : 's'}`,
    ]);
  }

  return (
    <>
      <div
        style={{
          position: 'relative',
          border: '2px solid #d1d5db',
          borderRadius: '10px',
          padding: '22px 24px',
          // Spacing comes from the grid gap in StaffRewards' section containers.
          backgroundColor: isActive ? 'white' : '#fafafa',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
              <span
                style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '17px',
                  fontWeight: 600,
                  color: '#111827',
                }}
              >
                {reward.title}
              </span>
              {reward.is_featured && (
                <span style={{ ...badge, backgroundColor: '#e0f2fe', color: '#075985' }}>
                  Featured
                </span>
              )}
              {isCompleted && <span style={badge}>Completed</span>}
              {!isActive && <span style={badge}>Inactive</span>}
            </div>
          </div>

          <div
            data-reward-menu={reward.id}
            style={{ position: 'relative', flexShrink: 0 }}
          >
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              style={{
                padding: '4px',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: 'none',
                borderRadius: '4px',
                fontFamily: '"Baikal Book"',
                fontSize: '24px',
                fontWeight: 'bold',
                cursor: 'pointer',
                width: '32px',
                height: '32px',
              }}
              title="Reward options"
            >
              ⋮
            </button>
            {menuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  minWidth: '150px',
                }}
              >
                <button
                  type="button"
                  style={menuItem}
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit(reward);
                  }}
                >
                  Edit
                </button>
                {onToggleStatus && (
                  <button
                    type="button"
                    style={{ ...menuItem, borderTop: '1px solid #e5e7eb' }}
                    onClick={() => {
                      setMenuOpen(false);
                      setShowConfirmModal(true);
                    }}
                  >
                    {isActive ? 'Deactivate' : 'Activate'}
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    style={{
                      ...menuItem,
                      borderTop: '1px solid #e5e7eb',
                      color: '#dc2626',
                    }}
                    onClick={() => {
                      setMenuOpen(false);
                      setShowDeleteModal(true);
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {(isRaffle || isAuction) && (
          <div style={{ marginTop: '12px' }}>
            <RedemptionPill
              type={isRaffle ? 'raffle' : 'auction'}
              drawingDate={reward.raffle_info?.drawing_date}
              endDate={reward.auction_info?.end_date}
              status={
                (isRaffle ? reward.raffle_info?.status : reward.auction_info?.status) || 'active'
              }
              isCompleted={isCompleted}
            />
          </div>
        )}

        {imageUrls.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              marginTop: '18px',
            }}
          >
            {imageUrls.map((url, index) => (
              <img
                key={`${url}-${index}`}
                src={url}
                alt=""
                title={index === 0 ? 'Shown first in the app' : `Image ${index + 1}`}
                style={{
                  width: '104px',
                  height: '104px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ))}
          </div>
        )}

        <div style={{ marginTop: '18px' }}>
          {detailRows.map(([fieldLabel, value]) => (
            <div
              key={fieldLabel}
              style={{
                display: 'grid',
                gridTemplateColumns: '140px 1fr',
                gap: '14px',
                padding: '10px 0',
                borderTop: '1px solid #f3f4f6',
                alignItems: 'start',
              }}
            >
              <span
                style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280',
                  lineHeight: 1.6,
                }}
              >
                {fieldLabel}
              </span>
              <span
                style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  color: value ? '#374151' : '#9ca3af',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {value || '—'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Confirmation Modal - Rendered outside the card to avoid opacity inheritance */}
      {showConfirmModal && onToggleStatus && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                marginBottom: '16px',
                color: '#111827',
              }}
            >
              {isActive ? 'Deactivate' : 'Activate'} Reward
            </h3>
            <p style={{ color: '#6B7280', marginBottom: '24px', lineHeight: '1.5' }}>
              Are you sure you want to {isActive ? 'deactivate' : 'activate'} &quot;
              {reward.title}&quot;?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowConfirmModal(false)} className="cancel-button">
                Cancel
              </button>
              <button
                onClick={() => {
                  onToggleStatus(reward.id, isActive);
                  setShowConfirmModal(false);
                }}
                className="staff-page-button"
                style={{
                  backgroundColor: isActive ? '#fca5a5' : '#86efac',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'white',
                }}
              >
                <span className="staff-page-button-text">
                  {isActive ? 'Deactivate' : 'Activate'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && onDelete && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                marginBottom: '16px',
                color: '#111827',
              }}
            >
              Delete Reward
            </h3>
            <p style={{ color: '#6B7280', marginBottom: '24px', lineHeight: '1.5' }}>
              Are you sure you want to delete &quot;{reward.title}&quot;? It will be removed from
              all reward lists. This cannot be undone from the dashboard.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDeleteModal(false)} className="cancel-button">
                Cancel
              </button>
              <button
                onClick={() => {
                  onDelete(reward.id);
                  setShowDeleteModal(false);
                }}
                className="staff-page-button"
                style={{
                  backgroundColor: '#dc2626',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'white',
                }}
              >
                <span className="staff-page-button-text">Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StaffRewardTile;
